import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Copy, Download, ExternalLink, Loader2, QrCode, RefreshCw, WifiOff } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { copyTextToClipboard } from "@/lib/clipboard";

function currentOrigin() {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  return window.location.origin;
}

function buildPublicUrl(token) {
  if (!token) return "";
  return `${currentOrigin()}/register/${token}`;
}

export function patientOnboardingQrFilename(clinicName) {
  const safeClinicName = String(clinicName || "clinic")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "clinic";
  return `${safeClinicName}_patient_onboarding_qr.svg`;
}

export default function PublicOnboardingLinkPanel({ surface = "shared", compact = false, defaultCollapsed = false }) {
  const { activeClinic } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const qrRef = useRef(null);

  async function loadSettings() {
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.rpc("get_self_registration_settings");
    setLoading(false);
    if (err) {
      setSettings(null);
      setError(err.message || "Could not load public link.");
      return;
    }
    setSettings(data || null);
  }

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    supabase.rpc("get_self_registration_settings").then(({ data, error: err }) => {
      if (!mounted) return;
      setLoading(false);
      if (err) {
        setSettings(null);
        setError(err.message || "Could not load public link.");
        return;
      }
      setSettings(data || null);
    });
    return () => { mounted = false; };
  }, []);

  const publicUrl = useMemo(() => buildPublicUrl(settings?.token), [settings?.token]);
  const clinicName = activeClinic?.clinic_name || activeClinic?.name || "clinic";

  async function copyLink() {
    if (!publicUrl) return;
    try {
      await copyTextToClipboard(publicUrl);
      setCopied(true);
      toast.success("Public link copied.");
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  }

  async function enableLink() {
    setBusy(true);
    const { data, error: err } = await supabase.rpc("set_self_registration_enabled", { p_enabled: true });
    setBusy(false);
    if (err) {
      toast.error(err.message || "Could not enable public link.");
      return;
    }
    setSettings(data || settings);
    toast.success("Public link enabled.");
  }

  function downloadQr() {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgText = new XMLSerializer().serializeToString(svg);
    const a = document.createElement("a");
    a.href = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgText)}`;
    a.download = patientOnboardingQrFilename(clinicName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const cardClass = compact
    ? "rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
    : "rounded-xl border border-slate-200 bg-white p-3 shadow-sm";

  return (
    <section className={cardClass} data-testid={`public-onboarding-link-${surface}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
          <QrCode className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-slate-900">Public onboarding link</h2>
            {settings?.enabled ? (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Active
              </span>
            ) : settings ? (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Disabled
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Patients register first, then request an appointment for staff confirmation.
          </p>
        </div>
        {loading ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0" /> : null}
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          data-testid="public-onboarding-toggle"
          className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800"
          aria-label={collapsed ? "Expand public onboarding link" : "Collapse public onboarding link"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {!collapsed && error ? (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700 flex items-start gap-2">
          <WifiOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
          <button type="button" onClick={loadSettings} className="ml-auto text-rose-700 hover:text-rose-900" aria-label="Retry public link">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}

      {!collapsed && publicUrl ? (
        <div className={`mt-3 grid gap-3 ${compact ? "grid-cols-1" : "sm:grid-cols-[minmax(0,1fr)_128px]"}`}>
          <div className="min-w-0">
            <label className="sr-only" htmlFor={`public-onboarding-url-${surface}`}>Public onboarding URL</label>
            <input
              id={`public-onboarding-url-${surface}`}
              data-testid="public-onboarding-url"
              readOnly
              value={publicUrl}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              onFocus={(event) => event.currentTarget.select()}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyLink}
                data-testid="public-onboarding-copy"
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open
              </a>
              <button
                type="button"
                onClick={downloadQr}
                disabled={!publicUrl}
                data-testid="public-onboarding-download-qr"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
              >
                <Download className="w-3.5 h-3.5" />
                QR
              </button>
              {!settings?.enabled ? (
                <button
                  type="button"
                  onClick={enableLink}
                  disabled={busy}
                  data-testid="public-onboarding-enable"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Enable
                </button>
              ) : null}
            </div>
          </div>
          {publicUrl ? (
            <div
              ref={qrRef}
              className="rounded-lg border border-slate-200 bg-white p-2 w-32 h-32 flex items-center justify-center"
              data-testid="public-onboarding-qr"
            >
              <QRCodeSVG
                value={publicUrl}
                size={112}
                includeMargin
                level="M"
                title="Patient onboarding QR"
              />
            </div>
          ) : null}
        </div>
      ) : !collapsed && !loading && !error ? (
        <p className="mt-3 text-xs text-slate-500">No public link available yet.</p>
      ) : null}
    </section>
  );
}
